import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

const AuthContext = createContext(null);

const VACIO = { ver: false, crear: false, editar: false, borrar: false };

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(null);
  const [loading, setLoading] = useState(true);

  // Verificar sesión activa al arrancar
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) { setLoading(false); return; }
    api.get('/auth/me')
      .then(({ data }) => setUser(data.user))
      .catch(() => localStorage.removeItem('access_token'))
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (username, password) => {
    // Obtener CSRF antes de login
    await api.get('/auth/csrf-token');
    const { data } = await api.post('/auth/login', { username, password });
    localStorage.setItem('access_token', data.accessToken);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try { await api.post('/auth/logout'); } catch { /* ignorar */ }
    localStorage.removeItem('access_token');
    setUser(null);
  }, []);

  // Recargar permisos sin cerrar sesión. Útil tras tocar roles o excepciones:
  // los cambios se ven al instante sin volver a entrar.
  const recargarPermisos = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      setUser(data.user);
      return data.user;
    } catch { return null; }
  }, []);

  // ─── Permisos ───────────────────────────────────────────────────
  // Los permisos llegan del backend como { codigo: {ver,crear,editar,borrar} }
  // Un rol con acceso_total (administrador) los tiene todos.
  const permisosDe = useCallback((codigo) => {
    if (!user) return VACIO;
    if (user.acceso_total) return { ver: true, crear: true, editar: true, borrar: true };
    return user.permisos?.[codigo] || VACIO;
  }, [user]);

  // puede('trafico', 'editar')  —  acción por defecto: 'ver'
  const puede = useCallback((codigo, accion = 'ver') => {
    return !!permisosDe(codigo)[accion];
  }, [permisosDe]);

  const puedeVer    = useCallback((c) => puede(c, 'ver'),    [puede]);
  const puedeCrear  = useCallback((c) => puede(c, 'crear'),  [puede]);
  const puedeEditar = useCallback((c) => puede(c, 'editar'), [puede]);
  const puedeBorrar = useCallback((c) => puede(c, 'borrar'), [puede]);

  // ¿Puede con alguno de los códigos indicados? Para pantallas y endpoints
  // compartidos (p.ej. la agenda, que son dos permisos distintos).
  const puedeAlguno = useCallback((codigos, accion = 'ver') => {
    return (codigos || []).some(c => puede(c, accion));
  }, [puede]);

  // ─── Rol (legado) ───────────────────────────────────────────────
  // Se mantiene para no romper código antiguo, pero en pantallas nuevas
  // hay que decidir por permiso, no por rol.
  const hasRol = useCallback((...roles) => {
    return user && roles.includes(user.rol);
  }, [user]);

  return (
    <AuthContext.Provider value={{
      user, loading, login, logout, recargarPermisos,
      permisosDe, puede, puedeVer, puedeCrear, puedeEditar, puedeBorrar, puedeAlguno,
      hasRol,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth debe usarse dentro de AuthProvider');
  return ctx;
}

// Atajo para pantallas: const p = usePermisos('clientes');
//   p.ver / p.crear / p.editar / p.borrar
export function usePermisos(codigo) {
  const { permisosDe } = useAuth();
  return permisosDe(codigo);
}
